import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import { DotStarburst } from "@/components/AboriginalPatterns";
import { WavyDotField } from "@/components/DecorativePatterns";
import { Shield } from "lucide-react";

const SectionBadge = ({ icon: Icon, label, color = "primary" }: { icon: any; label: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-2xl bg-${color}/8 border border-${color}/15`}>
      <Icon size={16} className={`text-${color}`} />
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] text-${color}`}>{label}</span>
  </div>
);

const WhatWeDoSection = () => (
  <section className="relative section-padding overflow-hidden">
    <Parallax className="absolute top-20 right-8" offset={-35}>
      <DotStarburst className="text-primary opacity-[0.06]" size={160} />
    </Parallax>
    <WavyDotField className="absolute top-8 right-0 text-primary opacity-[0.06] w-1/2" rows={4} cols={15} />
    <div className="max-w-3xl mx-auto px-6">
      <FadeIn>
        <SectionBadge icon={Shield} label="What We Do" color="primary" />
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 tracking-tight">
          Execution Durability,<br />
          <span className="text-muted-foreground">Not Culture Programs</span>
        </h2>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
          <p className="text-foreground font-medium">Boomerang Cultures gives acquirers visibility into execution risk before and after close.</p>
          <div className="pl-4 border-l-2 border-border/30 space-y-2 text-muted-foreground/70">
            <p>Not HR-led morale initiatives.</p>
            <p>Not generic values workshops.</p>
            <p>Not off-the-shelf leadership programs.</p>
          </div>
          <p className="text-foreground/80 font-medium">Pre-close:</p>
          <ul className="space-y-2 pl-1">
            {[
              "Assessing how the target's managers actually make decisions day to day",
              "Identifying where authority is exercised inconsistently across teams",
              "Surfacing the leadership habits that will determine integration success",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-foreground/80 font-medium">Post-close:</p>
          <ul className="space-y-2 pl-1">
            {[
              "Clarifying who decides what, and when to escalate",
              "Raising the bar on what's expected of managers during integration",
              "Fixing weak points before they become retention or performance problems",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/60 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-foreground font-medium">
            This is a <span className="text-gradient">leadership timing decision</span>, not a training event.
          </p>
          <div className="flex gap-6 text-foreground/70 font-medium">
            <span>Preventive.</span>
            <span>Deliberate.</span>
            <span>Evidence-based.</span>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

export default WhatWeDoSection;
